import { CodeHighlightNode, CodeNode } from "@lexical/code";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { TRANSFORMERS } from "@lexical/markdown";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { LexicalErrorBoundary } from "@lexical/react/LexicalErrorBoundary";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { MarkdownShortcutPlugin } from "@lexical/react/LexicalMarkdownShortcutPlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TablePlugin } from "@lexical/react/LexicalTablePlugin";
import { HeadingNode, QuoteNode } from "@lexical/rich-text";
import { TableCellNode, TableNode, TableRowNode } from "@lexical/table";
import ToolbarPlugin from "./plugins/ToolbarPlugin";

import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import DOMPurify from "dompurify";
import {
  $createParagraphNode,
  $getRoot,
  $isDecoratorNode,
  $isElementNode,
} from "lexical";
import {
  useDeferredValue,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { ImageNode } from "./nodes/ImageNode";
import LexicalAutoLinkPlugin from "./plugins/AutoLinkPlugin";
import CharacterLimitPlugin from "./plugins/CharacterLimitPlugin";
import CodeHighlightPlugin from "./plugins/CodeHighlightPlugin";
import EditablePlugin from "./plugins/EditablePlugin";
import { LinkClickPlugin } from "./plugins/LinkClickPlugin";
import ListMaxIndentLevelPlugin from "./plugins/ListMaxIndentLevelPlugin";
import TableActionMenuPlugin from "./plugins/TableActionMenuPlugin";
import TableCellResizerPlugin from "./plugins/TableCellResizerPlugin";
import TableHoverActionsPlugin from "./plugins/TableHoverActions";
import "./styles.css";
import Theme from "./Theme";

export function rteHasContent(html) {
  const temp = document.createElement("div");
  temp.innerHTML = DOMPurify.sanitize(html ?? "");
  return (
    temp.textContent.trim() !== "" ||
    temp.querySelectorAll("img").length > 0 ||
    temp.querySelectorAll("table").length > 0
  );
}

function sanitizeRTE(html) {
  return DOMPurify.sanitize(html, { ADD_ATTR: ["target"] });
}

function createEditorStateFromHTML(html) {
  return (editor) => {
    const parser = new DOMParser();
    const dom = parser.parseFromString(html, "text/html");

    const nodes = $generateNodesFromDOM(editor, dom);

    const root = $getRoot();
    root.clear();

    const wrappedNodes = nodes.map((node) => {
      if ($isElementNode(node) || $isDecoratorNode(node)) {
        return node;
      }
      const paragraph = $createParagraphNode();
      paragraph.append(node);
      return paragraph;
    });

    root.append(...wrappedNodes);
  };
}

/**
 * RichTextEditor — Lexical-based rich text editor with toolbar, tables, images,
 * links, code blocks, and markdown shortcuts.
 *
 * For display-only content, use RichTextViewer instead.
 *
 * Props:
 * @param {string}        [id] — HTML id used to associate the label with the editor via aria-labelledby.
 * @param {React.ReactNode} [label]               — Visible label rendered above the editor. When provided, the editor uses aria-labelledby for accessibility.
 * @param {string}        [ariaLabel]             — Screen-reader-only label. Used as aria-label when no visible label is provided. At least one of `label` or `ariaLabel` is required.
 * @param {string}        [defaultValue]          — Initial HTML content. Parsed once on mount; subsequent changes are ignored (uncontrolled). Supports both HTML strings and plain text.
 * @param {function}      [onChange]              — Callback fired with the current HTML string when content changes. Uses useDeferredValue to avoid blocking input.
 * @param {boolean}       [uneditable=false]      — Keeps the toolbar visible but disables all toolbar buttons and prevents typing. Use when the editor should look editable but the user cannot modify content (e.g. template previews).
 * @param {boolean}       [showTable=true]        — Whether to show the table insert button in the toolbar.
 * @param {boolean}       [showImage=true]        — Whether to show the image insert button in the toolbar.
 * @param {number}        [charLimit]             — Optional character limit. Displays a counter in the bottom-right corner. The counter turns red when the limit is met or exceeded. Typing is not blocked.
 * @param {function}      [onCharLimitReached]        — Optional callback fired with a boolean: `true` when the character count meets or exceeds `charLimit`, `false` otherwise. Only fires when `charLimit` is set.
 *
 * Utility exports:
 * - rteHasContent(html)            — Returns true if the HTML contains meaningful text, images, or tables.
 */
const RichTextEditor = ({
  id,
  label,
  ariaLabel,
  defaultValue,
  onChange,
  uneditable = false,
  showTable = true,
  showImage = true,
  charLimit,
  onCharLimitReached,
}) => {
  if (process.env.NODE_ENV !== "production" && !label && !ariaLabel) {
    console.error(
      "RichTextEditor: either `label` or `ariaLabel` is required for accessibility."
    );
  }

  const generatedId = useId();
  const labelId = id || generatedId;
  const [floatingAnchorElem, setFloatingAnchorElem] = useState(null);
  const onRef = (_floatingAnchorElem) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };
  const [inputValue, setInputValue] = useState("");
  const deferredInputValue = useDeferredValue(inputValue);

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  useEffect(() => {
    if (
      deferredInputValue !== "" &&
      typeof onChangeRef.current === "function"
    ) {
      onChangeRef.current(deferredInputValue);
    }
  }, [deferredInputValue]);

  const initialConfig = useMemo(
    () => ({
      editable: !uneditable,
      // The editor initial value
      editorState: defaultValue
        ? createEditorStateFromHTML(defaultValue)
        : undefined,
      // The editor theme
      theme: Theme,
      // Handling of errors during update
      onError(error) {
        throw error;
      },
      // Any custom nodes go here
      nodes: [
        HeadingNode,
        ListNode,
        ListItemNode,
        QuoteNode,
        CodeNode,
        CodeHighlightNode,
        TableNode,
        TableCellNode,
        TableRowNode,
        AutoLinkNode,
        LinkNode,
        ImageNode,
      ],
    }),
    [uneditable]
  );

  const editorRef = useRef(null);

  return (
    <div>
      {label && (
        <span id={labelId} onClick={() => editorRef.current?.focus()}>
          {label}
        </span>
      )}
      <LexicalComposer initialConfig={initialConfig}>
        <div
          style={{ marginTop: label ? "0.75rem" : 0 }}
          className="editor-container"
        >
          <ToolbarPlugin
            disabled={uneditable}
            showTable={showTable}
            showImage={showImage}
          />
          <div className="editor-inner">
            <RichTextPlugin
              contentEditable={
                <div ref={onRef}>
                  <ContentEditable
                    ref={editorRef}
                    {...(label
                      ? { "aria-labelledby": labelId }
                      : { "aria-label": ariaLabel })}
                    className="editor-input"
                  />
                </div>
              }
              ErrorBoundary={LexicalErrorBoundary}
            />
            <HistoryPlugin />
            <EditablePlugin uneditable={uneditable} />
            <CodeHighlightPlugin />
            <ListPlugin />
            <TabIndentationPlugin />
            <LinkPlugin />
            <LinkClickPlugin />
            <LexicalAutoLinkPlugin />
            <ListMaxIndentLevelPlugin maxDepth={7} />
            <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
            <TablePlugin hasHorizontalScroll={true} />
            {floatingAnchorElem && (
              <>
                <TableCellResizerPlugin anchorElem={floatingAnchorElem} />
                <TableHoverActionsPlugin anchorElem={floatingAnchorElem} />
                <TableActionMenuPlugin
                  anchorElem={floatingAnchorElem}
                  cellMerge={true}
                />
              </>
            )}
            {onChange && (
              <OnChangePlugin
                onChange={(editorState, editor) => {
                  editorState.read(() => {
                    const html = $generateHtmlFromNodes(editor);
                    const sanitizedHtml = sanitizeRTE(html);
                    setInputValue(sanitizedHtml);
                  });
                }}
              />
            )}
            {charLimit != null && (
              <CharacterLimitPlugin
                charLimit={charLimit}
                onLimitReached={onCharLimitReached}
              />
            )}
          </div>
        </div>
      </LexicalComposer>
    </div>
  );
};

export default RichTextEditor;
